import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Autocomplete, Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MainCard from 'ui-component/cards/MainCard';
import { getKpi, getKpis, postKpiData } from 'services/kpi';
import { canAccessKpiMenu, KPI_ACCENT } from './kpiUtils';
import KpiFilters from './KpiFilters';
import KpiMetricCard from './widgets/KpiMetricCard';
import KpiGroupCard from './widgets/KpiGroupCard';
import KpiChartCard from './widgets/KpiChartCard';
import KpiTableCard from './widgets/KpiTableCard';

const emptyFilters = (filtros = []) =>
    filtros.reduce((acc, filtro) => {
        const tipo = (filtro.tipo || '').toLowerCase();
        if (tipo === 'multiselect') acc[filtro.chave] = [];
        else if (tipo === 'daterange') acc[filtro.chave] = { inicio: null, fim: null };
        else acc[filtro.chave] = null;
        return acc;
    }, {});

const renderItem = (item) => {
    const tipo = (item.tipo || '').toLowerCase();
    if (item.error && tipo !== 'bar' && tipo !== 'line' && tipo !== 'area' && tipo !== 'pie' && tipo !== 'donut' && tipo !== 'gauge') {
        return (
            <Grid item xs={12} key={item.id}>
                <Alert severity="warning">{item.titulo ? `${item.titulo}: ${item.error}` : item.error}</Alert>
            </Grid>
        );
    }
    if (tipo === 'card') return <KpiMetricCard key={item.id} item={item} />;
    if (tipo === 'kpi_group') return <KpiGroupCard key={item.id} item={item} />;
    if (tipo === 'table') return <KpiTableCard key={item.id} item={item} />;
    return <KpiChartCard key={item.id} item={item} />;
};

const KpiDashboard = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const allowed = canAccessKpiMenu(user);

    const [definition, setDefinition] = useState(null);
    const [availableKpis, setAvailableKpis] = useState([]);
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [error, setError] = useState('');

    const loadData = useCallback(
        async (nextFilters) => {
            if (!slug) return;
            setLoadingData(true);
            setError('');
            try {
                const response = await postKpiData(slug, nextFilters || {});
                setItems(response.data?.data?.itens || []);
            } catch (err) {
                const message = err?.response?.data?.message || 'Não foi possível carregar os indicadores.';
                setError(message);
                setItems([]);
            } finally {
                setLoadingData(false);
                setLoading(false);
            }
        },
        [slug]
    );

    useEffect(() => {
        if (!allowed) return undefined;
        let active = true;
        getKpis()
            .then((response) => {
                if (active) setAvailableKpis(response.data?.data || []);
            })
            .catch(() => {
                if (active) setAvailableKpis([]);
            });
        return () => {
            active = false;
        };
    }, [allowed]);

    useEffect(() => {
        if (!allowed || !slug) return undefined;
        let active = true;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await getKpi(slug);
                if (!active) return;
                const data = response.data?.data;
                setDefinition(data);
                const initial = emptyFilters(data?.filtros || []);
                setFilters(initial);
                await loadData(initial);
            } catch (err) {
                if (!active) return;
                setDefinition(null);
                setItems([]);
                setError(err?.response?.data?.message || 'KPI não encontrado.');
                setLoading(false);
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [slug, allowed, loadData]);

    const title = useMemo(() => definition?.titulo || 'KPI', [definition]);
    const selectedKpi = useMemo(
        () => availableKpis.find((kpi) => kpi.slug === slug) || null,
        [availableKpis, slug]
    );

    if (!allowed) {
        return <Navigate to="/index" replace />;
    }

    return (
        <MainCard
            title={
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/kpis')}
                        sx={{ color: KPI_ACCENT, mb: 1, px: 0, textTransform: 'none', fontWeight: 700 }}
                    >
                        Central de KPIs
                    </Button>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="overline" sx={{ color: KPI_ACCENT, fontWeight: 700, letterSpacing: 1.4 }}>
                                Indicadores
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 800, color: '#2A1638' }}>
                                {title}
                            </Typography>
                            {definition?.descricao && (
                                <Typography variant="body2" sx={{ color: '#6B5A78', mt: 0.5 }}>
                                    {definition.descricao}
                                </Typography>
                            )}
                        </Box>
                        {availableKpis.length > 1 && (
                            <Autocomplete
                                sx={{ minWidth: 280 }}
                                options={availableKpis}
                                value={selectedKpi}
                                getOptionLabel={(option) => option?.titulo || ''}
                                isOptionEqualToValue={(option, value) => option?.slug === value?.slug}
                                onChange={(_, next) => {
                                    if (next?.slug) navigate(`/kpis/${next.slug}`);
                                }}
                                renderInput={(params) => <TextField {...params} size="small" label="Trocar KPI" />}
                            />
                        )}
                    </Box>
                </Box>
            }
        >
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: KPI_ACCENT }} />
                </Box>
            )}

            {!loading && (
                <>
                    <KpiFilters
                        filtros={definition?.filtros || []}
                        values={filters}
                        onChange={setFilters}
                        onApply={() => loadData(filters)}
                        loading={loadingData}
                    />
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {loadingData && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
                            <CircularProgress size={28} sx={{ color: KPI_ACCENT }} />
                        </Box>
                    )}
                    <Grid container spacing={2.5}>
                        {items.map(renderItem)}
                    </Grid>
                    {!loadingData && items.length === 0 && !error && (
                        <Box sx={{ py: 6, textAlign: 'center', color: '#8A7A98' }}>Nenhum indicador configurado neste KPI.</Box>
                    )}
                </>
            )}
        </MainCard>
    );
};

export default KpiDashboard;
